import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getRequest } from "../../system/server-call";
import { ToastifyService } from "../../services";
import { getUserData } from "../../system";

const PlatformDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState([]);
  const currentUser = getUserData();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      const response = await getRequest("companies");
      if (response.data && response.data.data) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      ToastifyService.showError(error.response?.data?.message || "Failed to fetch companies");
    } finally {
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const getLicenseStatus = (company) => {
    if (!company.is_active) return { text: "Inactive", variant: "secondary" };
    const end = new Date(company.license_end_date);
    const now = new Date();
    if (end < now) return { text: "Expired", variant: "danger" };
    
    // Check if expiring in 7 days
    const diff = end.getTime() - now.getTime();
    const days = diff / (1000 * 3600 * 24);
    if (days <= 7) return { text: "Expiring soon", variant: "warning" };
    
    return { text: "Active", variant: "success" };
  };

  // Helper to compute stats
  const totalCount = companies.length;
  const activeCount = companies.filter(c => c.is_active).length;
  const expiredCount = companies.filter(c => c.license_end_date && new Date(c.license_end_date) < new Date()).length;
  const totalUsers = companies.reduce((acc, curr) => acc + (curr.users_count || 0), 0);

  // Chart Data Calculations
  const monthlyCount = companies.filter(c => c.license_type === "monthly").length;
  const yearlyCount = companies.filter(c => c.license_type === "yearly").length;

  const licenseTypeData = [
    { name: "Monthly", value: monthlyCount },
    { name: "Yearly", value: yearlyCount },
  ];

  const activeStatsCount = companies.filter(c => getLicenseStatus(c).text === "Active").length;
  const expiringSoonStatsCount = companies.filter(c => getLicenseStatus(c).text === "Expiring soon").length;
  const expiredStatsCount = companies.filter(c => getLicenseStatus(c).text === "Expired").length;
  const inactiveStatsCount = companies.filter(c => getLicenseStatus(c).text === "Inactive").length;

  const licenseStatusData = [
    { name: "Active", value: activeStatsCount },
    { name: "Expiring Soon", value: expiringSoonStatsCount },
    { name: "Expired", value: expiredStatsCount },
    { name: "Inactive", value: inactiveStatsCount },
  ];

  // Colors
  const PIE_COLORS = ["#0284c7", "#0d9488"];

  // Sort companies by creation or id to show recent ones
  const recentCompanies = [...companies]
    .sort((a, b) => b.id - a.id)
    .slice(0, 5);

  if (!currentUser || !currentUser.is_super_admin) {
    return <div className="p-4 text-center"><h3>Access Denied. Super Admin only.</h3></div>;
  }

  return (
    <div className="container-fluid py-2">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
        <div>
          <h2 className="text-dark fw-bold m-0" style={{ letterSpacing: "-0.5px" }}>Platform Admin Control Panel</h2>
          <p className="text-muted m-0" style={{ fontSize: "0.95rem" }}>System health, subscription analytics, and tenant management</p>
        </div>
        <button 
          onClick={() => navigate("/companies")} 
          className="btn btn-primary shadow-sm px-4 py-2 fw-semibold d-flex align-items-center gap-2"
          style={{ borderRadius: "10px" }}
        >
          <i className="bi bi-gear-fill"></i> Manage Companies
        </button>
      </div>

      {/* Stats Cards Row */}
      <div className="row mb-4">
        {/* Total Companies */}
        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-white kpi-card premium-shadow">
            <div className="card-body d-flex align-items-center p-3">
              <div className="bg-primary bg-opacity-10 p-2.5 rounded-3 me-3 text-primary icon-box d-flex align-items-center justify-content-center" style={{ width: "45px", height: "45px" }}>
                <i className="bi bi-building fs-4"></i>
              </div>
              <div>
                <h6 className="text-muted text-uppercase mb-1 fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Total Companies</h6>
                <h3 className="fw-bold m-0 text-dark" style={{ fontSize: "1.4rem", letterSpacing: "-0.5px" }}>{totalCount}</h3>
              </div>
            </div>
          </div>
        </div>
        
        {/* Active Subscriptions */}
        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-white kpi-card premium-shadow">
            <div className="card-body d-flex align-items-center p-3">
              <div className="bg-success bg-opacity-10 p-2.5 rounded-3 me-3 text-success icon-box d-flex align-items-center justify-content-center" style={{ width: "45px", height: "45px" }}>
                <i className="bi bi-patch-check-fill fs-4"></i>
              </div>
              <div>
                <h6 className="text-muted text-uppercase mb-1 fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Active Licenses</h6>
                <h3 className="fw-bold m-0 text-dark" style={{ fontSize: "1.4rem", letterSpacing: "-0.5px" }}>{activeCount - expiredCount}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Expired Licenses */}
        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-white kpi-card premium-shadow">
            <div className="card-body d-flex align-items-center p-3">
              <div className="bg-danger bg-opacity-10 p-2.5 rounded-3 me-3 text-danger icon-box d-flex align-items-center justify-content-center" style={{ width: "45px", height: "45px" }}>
                <i className="bi bi-exclamation-octagon-fill fs-4"></i>
              </div>
              <div>
                <h6 className="text-muted text-uppercase mb-1 fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Expired Licenses</h6>
                <h3 className="fw-bold m-0 text-dark" style={{ fontSize: "1.4rem", letterSpacing: "-0.5px" }}>{expiredCount}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Total Users */}
        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-white kpi-card premium-shadow">
            <div className="card-body d-flex align-items-center p-3">
              <div className="bg-info bg-opacity-10 p-2.5 rounded-3 me-3 text-info icon-box d-flex align-items-center justify-content-center" style={{ width: "45px", height: "45px" }}>
                <i className="bi bi-people-fill fs-4"></i>
              </div>
              <div>
                <h6 className="text-muted text-uppercase mb-1 fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Total Admin Users</h6>
                <h3 className="fw-bold m-0 text-dark" style={{ fontSize: "1.4rem", letterSpacing: "-0.5px" }}>{totalUsers}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-4">
        {/* Left: Bar Chart of License Status */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4 bg-white premium-shadow h-100">
            <div className="card-body p-3">
              <div className="mb-3">
                <h6 className="mb-0 text-dark fw-bold" style={{ fontSize: "1rem" }}>License Status Distribution</h6>
                <p className="text-muted m-0" style={{ fontSize: "0.8rem" }}>Categorized status of all registered company licenses</p>
              </div>
              
              <div style={{ height: 230 }}>
                {companies.length === 0 ? (
                  <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={licenseStatusData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.85}/>
                          <stop offset="95%" stopColor="#15803d" stopOpacity={0.25}/>
                        </linearGradient>
                        <linearGradient id="colorExpiring" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#eab308" stopOpacity={0.85}/>
                          <stop offset="95%" stopColor="#a16207" stopOpacity={0.25}/>
                        </linearGradient>
                        <linearGradient id="colorExpired" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.85}/>
                          <stop offset="95%" stopColor="#b91c1c" stopOpacity={0.25}/>
                        </linearGradient>
                        <linearGradient id="colorInactive" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.85}/>
                          <stop offset="95%" stopColor="#475569" stopOpacity={0.25}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                      <Tooltip 
                        cursor={{ fill: "rgba(148, 163, 184, 0.05)" }}
                        contentStyle={{ backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #f1f5f9", boxShadow: "0 6px 12px -3px rgba(0, 0, 0, 0.03)", fontSize: "0.8rem" }}
                      />
                      <Legend verticalAlign="top" height={28} iconType="circle" fontSize={10} />
                      <Bar dataKey="value" name="Companies" radius={[4, 4, 0, 0]}>
                        {licenseStatusData.map((entry, index) => {
                          const gradients = ["url(#colorActive)", "url(#colorExpiring)", "url(#colorExpired)", "url(#colorInactive)"];
                          return <Cell key={`cell-${index}`} fill={gradients[index % gradients.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right: Pie Chart of License Type */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm rounded-4 bg-white premium-shadow h-100">
            <div className="card-body p-3">
              <div>
                <h6 className="mb-0 text-dark fw-bold" style={{ fontSize: "1rem" }}>Subscription Model Breakdown</h6>
                <p className="text-muted m-0" style={{ fontSize: "0.8rem" }}>Percentage breakdown of Monthly vs Yearly licenses</p>
              </div>
              
              <div style={{ height: 230 }} className="d-flex flex-column align-items-center justify-content-center">
                {companies.length === 0 ? (
                  <div className="text-muted">No data available</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height="80%">
                      <PieChart>
                        <defs>
                          <linearGradient id="colorMonthly" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="5%" stopColor="#0284c7" stopOpacity={0.9}/>
                            <stop offset="95%" stopColor="#0369a1" stopOpacity={0.65}/>
                          </linearGradient>
                          <linearGradient id="colorYearly" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="5%" stopColor="#0d9488" stopOpacity={0.9}/>
                            <stop offset="95%" stopColor="#0f766e" stopOpacity={0.65}/>
                          </linearGradient>
                        </defs>
                        <Pie
                          data={licenseTypeData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={4}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          style={{ fontSize: "10px" }}
                        >
                          {licenseTypeData.map((entry, index) => {
                            const gradients = ["url(#colorMonthly)", "url(#colorYearly)"];
                            return <Cell key={`cell-${index}`} fill={gradients[index % gradients.length]} />;
                          })}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Custom Legend */}
                    <div className="d-flex gap-3 mt-1">
                      {licenseTypeData.map((entry, index) => (
                        <div key={entry.name} className="d-flex align-items-center gap-1.5">
                          <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: PIE_COLORS[index], display: "inline-block" }}></span>
                          <span className="text-dark" style={{ fontSize: "0.8rem", fontWeight: "500" }}>
                            {entry.name}: <strong className="text-dark">{entry.value}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row: Recent Companies Table */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4 bg-white premium-shadow">
            <div className="card-body p-3.5">
              <div className="mb-3">
                <h6 className="text-dark fw-bold mb-0" style={{ fontSize: "1rem" }}>Recently Added Companies</h6>
                <p className="text-muted m-0" style={{ fontSize: "0.8rem" }}>The latest 5 company accounts registered on the platform</p>
              </div>
              
              <div className="table-responsive">
                <table className="table hover borderless align-middle mb-0" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr className="text-muted" style={{ borderBottom: "1px solid rgba(0,0,0,0.08)", fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      <th style={{ padding: "12px 16px" }}>Company Name</th>
                      <th style={{ padding: "12px 16px" }}>Email</th>
                      <th style={{ padding: "12px 16px" }}>License Type</th>
                      <th style={{ padding: "12px 16px" }}>License Status</th>
                      <th style={{ padding: "12px 16px" }}>Users Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCompanies.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">No registered companies found</td>
                      </tr>
                    ) : (
                      recentCompanies.map((company) => {
                        const status = getLicenseStatus(company);
                        return (
                          <tr key={company.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                            <td className="fw-semibold text-dark py-3" style={{ padding: "12px 16px" }}>{company.name}</td>
                            <td className="text-dark" style={{ padding: "12px 16px" }}>{company.email || "N/A"}</td>
                            <td style={{ padding: "12px 16px" }}>
                              <span className="badge bg-light text-dark text-capitalize py-1.5 px-2.5 rounded-pill border border-light" style={{ fontSize: "0.7rem" }}>
                                {company.license_type}
                              </span>
                            </td>
                            <td style={{ padding: "12px 16px" }}>
                              <span className={`badge bg-${status.variant} py-1.5 px-2.5 rounded-pill`} style={{ fontSize: "0.7rem" }}>
                                {status.text}
                              </span>
                            </td>
                            <td className="text-dark fw-medium" style={{ padding: "12px 16px" }}>{company.users_count || 0}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .kpi-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
        }
        .kpi-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 15px -6px rgba(0, 0, 0, 0.06) !important;
        }
        .icon-box {
          transition: all 0.3s ease;
        }
        .kpi-card:hover .icon-box {
          transform: scale(1.06) rotate(3deg);
        }
        .premium-shadow {
          box-shadow: 0 4px 12px 0 rgba(0,0,0,0.015), 0 1px 2px 0 rgba(0,0,0,0.01) !important;
        }
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .transition-all:hover {
          background-color: #f1f5f9 !important;
          transform: translateX(3px);
        }
      `}</style>
    </div>
  );
};

export default PlatformDashboard;
